import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton, Tooltip, Checkbox, CircularProgress, Alert, Chip } from '@mui/material';
import { Add as AddIcon, DeleteOutlined as DeleteIcon, ListAltOutlined as EditIcon, InfoOutlined as InfoIcon, FilterListOutlined as FilterIcon, ForwardToInbox as ResendIcon, Autorenew as ResetIcon } from '@mui/icons-material';
import AddUserModal from '../components/AddUserModal';
import { ConfirmationDialog } from '../components/ConfirmationDialog';
import UserDetailsModal from '../components/UserDetailsModal';
import { UserAPI } from '../apis/UserAPI';
import { useRolesList } from '../components/ApplicationContext';
import { isAuthorized } from '../utils/utilities';
import authenticatedRoute from '../views/AuthenticatedRoute';
import { RBAC_CONSTANTS, constants } from '../constants';
import { useThemeDetails } from '../components/ThemeDetailsContext';
// Accepts E.164 (+15551234567), national (555-123-4567), and common formatted
// variants.  Requires at least 7 digits so that empty / clearly-invalid strings
// are rejected before they reach the API.
const isValidPhoneNumber = (phone) => {
    if (!phone?.trim())
        return false;
    // JSON format produced by AddUserModal/SettingsModal: {"country":"us","dialCode":"1","number":"5551234567"}
    if (phone.startsWith('{')) {
        try {
            const parsed = JSON.parse(phone);
            return Boolean(parsed?.number);
        }
        catch {
            return false;
        }
    }
    // Legacy plain-string format
    const digits = phone.replaceAll(/\D/g, '');
    return digits.length >= 7 && /^\+?[\d\s\-().]{7,20}$/.test(phone.trim());
};
// Helper function to get status chip background color
const getStatusBackgroundColor = (status) => {
    if (status === RBAC_CONSTANTS.rbac_user_status_active) {
        return '#f3f8eb';
    }
    if (status === RBAC_CONSTANTS.rbac_user_status_pending) {
        return '#fef6ea';
    }
    return '#fce4ec';
};
// Helper function to get status chip text color
const getStatusTextColor = (status) => {
    if (status === RBAC_CONSTANTS.rbac_user_status_active) {
        return '#1a5d09';
    }
    if (status === RBAC_CONSTANTS.rbac_user_status_pending) {
        return '#9f6514';
    }
    return '#c62828';
};
// Helper function to correct user roles (extracted to reduce nesting)
const correctUserRoles = (user, roles) => {
    const userRoles = Array.isArray(user.role) ? user.role : [user.role];
    // Dedupe roles first and filter empty values
    const uniqueRoles = Array.from(new Set(userRoles)).filter((role) => role && role.trim() !== '');
    const validRoles = uniqueRoles.filter((role) => roles.some((availableRole) => availableRole.role_name === role));
    if (validRoles.length === 0) {
        // Don't assign a default role - show empty if user has no valid roles
        return { ...user, role: [] };
    }
    if (validRoles.length !== uniqueRoles.length) {
        console.warn(`User ${user.email} has some invalid roles, keeping only valid ones: "${validRoles.join(', ')}"`);
        return { ...user, role: validRoles };
    }
    return { ...user, role: uniqueRoles };
};
// Helper function to process a single user
const processSingleUser = async (userData, selectedUser, validateUserData, validateUserRoles, updateExistingUser, createNewUser) => {
    let userRoles = [];
    if (Array.isArray(userData.role)) {
        userRoles = Array.from(new Set(userData.role)).filter(Boolean); // Dedupe and filter empty strings
    }
    else if (userData.role) {
        userRoles = [userData.role];
    }
    else {
        userRoles = [];
    }
    validateUserData(userData, selectedUser);
    // Only validate roles if roles are provided
    const selectedRoleObjs = userRoles.length > 0 ? validateUserRoles(userRoles) : [];
    if (selectedUser) {
        await updateExistingUser(userData, selectedRoleObjs);
        return null; // No new user to return for updates
    }
    else {
        const newUser = await createNewUser(userData, selectedRoleObjs);
        return {
            id: newUser.id,
            avatarUrl: userData.avatarUrl ?? '',
            name: userData.name,
            email: userData.email,
            ...(isValidPhoneNumber(userData.phone) ? { phone: userData.phone } : {}),
            role: userRoles,
            groups: [], // New users have no groups initially
            status: userData.status ?? 'active',
            dateAdded: new Date().toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
                year: 'numeric'
            }),
            isExternal: userData.isExternal ?? false,
            extUserId: newUser.id
        };
    }
};
// Helper function to handle save results
const handleSaveResults = (newUsers, errors, setUsers, setUserModalError, setUserModalOpen, isUpdate) => {
    if (newUsers.length > 0) {
        setUsers((prevUsers) => [...prevUsers, ...newUsers]);
    }
    if (errors.length > 0 && newUsers.length > 0) {
        setUserModalError(constants.team_access_error_some_users_could_not_be_added(errors.join(', ')));
        // Keep modal open when there are errors
    }
    else if (errors.length > 0 && (newUsers.length === 0 || isUpdate)) {
        setUserModalError(constants.team_access_error_failed_to_add_users(errors.join(', ')));
        // Keep modal open when there are errors
    }
    else if (newUsers.length > 0 || isUpdate) {
        console.log(isUpdate
            ? 'Successfully updated user'
            : `Successfully added ${newUsers.length} user(s)`);
        setUserModalError(null);
        setUserModalOpen(false);
    }
};
const TeamAccess = () => {
    const { stateImages } = useThemeDetails();
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [selectedUsers, setSelectedUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(true);
    const [error, setError] = useState(null);
    const [userModalError, setUserModalError] = useState(null);
    // Modal and dialog state
    const [userModalOpen, setUserModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [userDetailsModalOpen, setUserDetailsModalOpen] = useState(false);
    const [selectedUserForDetails, setSelectedUserForDetails] = useState(null);
    // Load data on component mount
    useEffect(() => {
        loadInitialData();
    }, []);
    const getRoles = useRolesList();
    const loadInitialData = async () => {
        setDataLoading(true);
        setError(null);
        try {
            // Load applications, users with roles and roles in parallel
            const [usersWithRolesResponse, rolesResource] = await Promise.all([
                UserAPI.getUsersWithRoles(),
                getRoles()
            ]);
            setRoles(rolesResource);
            // Transform the aggregated user data to match the existing format
            const usersWithRoles = usersWithRolesResponse.resource.map((user) => {
                const userRoles = user.roles?.map((roleObj) => roleObj.role_name) ?? [];
                const userStatus = user.userAttributes?.status;
                const status = userStatus === RBAC_CONSTANTS.rbac_user_status_active ||
                    userStatus ===
                        RBAC_CONSTANTS.rbac_user_status_pending ||
                    userStatus === RBAC_CONSTANTS.rbac_user_status_inactive
                    ? userStatus
                    : RBAC_CONSTANTS.rbac_user_status_active;
                // Get user groups
                const userGroups = user.groups?.map((groupObj) => groupObj.group_name) ??
                    [];
                return {
                    id: user.id,
                    avatarUrl: '',
                    name: user.userAttributes?.name ?? user.username,
                    email: user.userAttributes?.email ?? user.username,
                    phone: user.userAttributes?.phone,
                    role: userRoles, // Support multiple roles
                    groups: userGroups, // User groups
                    status,
                    dateAdded: user.metadata?.createdDate
                        ? new Date(user.metadata.createdDate).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                        })
                        : constants.team_access_date_unknown,
                    isExternal: user.userAttributes?.isExternal ?? false,
                    extUserId: user.userAttributes?.extUserId
                };
            });
            setUsers(usersWithRoles);
        }
        catch (err) {
            console.error('Failed to load data:', err);
            setError(constants.team_access_error_load_users_roles);
        }
        finally {
            setDataLoading(false);
        }
    };
    // Effect to correct user roles when roles are loaded
    useEffect(() => {
        if (roles.length > 0 && users.length > 0) {
            const correctedUsers = users.map((user) => correctUserRoles(user, roles));
            // Only update if there were changes
            const hasChanges = correctedUsers.some((user, index) => {
                const originalRoles = Array.isArray(users[index].role)
                    ? users[index].role
                    : [users[index].role];
                const correctedRoles = Array.isArray(user.role)
                    ? user.role
                    : [user.role];
                return (JSON.stringify([...originalRoles].sort((a, b) => String(a).localeCompare(String(b)))) !==
                    JSON.stringify([...correctedRoles].sort((a, b) => String(a).localeCompare(String(b)))));
            });
            if (hasChanges) {
                setUsers(correctedUsers);
            }
        }
    }, [roles, users]);
    // Handler functions
    const handleAddUser = () => {
        setSelectedUser(null);
        setUserModalOpen(true);
    };
    // Bulk delete handler
    const handleBulkDeleteUsers = async () => {
        setLoading(true);
        setError(null);
        try {
            for (const userId of selectedUsers) {
                const user = users.find((u) => u.id === userId);
                if (user) {
                    await UserAPI.deleteUser(user.id);
                }
            }
            setUsers((prevUsers) => prevUsers.filter((u) => !selectedUsers.includes(u.id)));
            setSelectedUsers([]);
        }
        catch (err) {
            console.error('Failed to bulk delete users:', err);
            setError(constants.team_access_error_delete_user);
        }
        finally {
            setLoading(false);
        }
    };
    const handleEditUser = (user) => {
        setSelectedUser(user);
        setUserModalOpen(true);
    };
    const handleViewUserDetails = (user) => {
        setSelectedUserForDetails(user);
        setUserDetailsModalOpen(true);
    };
    const handleDeleteUser = (user) => {
        setUserToDelete(user);
        setConfirmDialogOpen(true);
    };
    // Helper function to validate and find role objects
    const validateUserRoles = (userRoles) => {
        return userRoles.map((roleName) => {
            const roleObj = roles.find((role) => role.role_name === roleName);
            if (!roleObj) {
                throw new Error(`Role '${roleName}' not found`);
            }
            return roleObj;
        });
    };
    // Helper function to validate user data
    const validateUserData = (userData, editingUser) => {
        if (!userData.email?.includes('@')) {
            throw new Error('Invalid email address');
        }
        const existingUser = users.find((u) => u.email === userData.email);
        // Skip check if we're editing the same user
        if (existingUser && existingUser.id !== editingUser?.id) {
            throw new Error('User already exists');
        }
    };
    // Helper function to create a new user
    const createNewUser = async (userData, selectedRoleObjs) => {
        const createData = {
            resourceType: 'User',
            username: userData.email,
            organization_id: 1,
            principal_type: 1,
            userAttributes: {
                name: userData.name,
                email: userData.email,
                ...(isValidPhoneNumber(userData.phone) ? { phone: userData.phone } : {})
            }
        };
        const response = await UserAPI.createUser(createData);
        const newUser = response;
        if (!newUser?.id || !newUser?.username) {
            throw new Error('User creation succeeded but returned incomplete user data');
        }
        // Small delay to ensure user is fully created before role assignment
        await new Promise((resolve) => setTimeout(resolve, 100));
        // Assign each role sequentially (only if roles are provided)
        for (const roleObj of selectedRoleObjs) {
            await UserAPI.assignRole(newUser.username, {
                roleId: Number.parseInt(roleObj.role_id)
            });
        }
        return newUser;
    };
    // Helper function to update existing user
    // Fetches current user data, merges only updated fields, and sends entire payload
    const updateExistingUser = async (userData, 
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    _selectedRoleObjs) => {
        // Fetch current user data to get the full resource
        const currentUserResponse = await UserAPI.getUsers(userData.email);
        const currentUserData = currentUserResponse?.resource?.[0];
        if (!currentUserData) {
            throw new Error('Unable to fetch current user data');
        }
        // Merge only updated fields into existing user resource
        const updatedResource = {
            ...currentUserData,
            userAttributes: {
                ...currentUserData.userAttributes,
                name: userData.name,
                email: userData.email,
                ...(isValidPhoneNumber(userData.phone) ? { phone: userData.phone } : {})
            }
        };
        await UserAPI.updateUser(selectedUser.id, updatedResource);
        // Update local state - only update specific fields, preserve role and status
        setUsers((prevUsers) => prevUsers.map((u) => u.id === selectedUser.id
            ? {
                ...u,
                name: userData.name,
                email: userData.email,
                phone: userData.phone
            }
            : u));
    };
    const handleSaveUser = async (usersData) => {
        setLoading(true);
        setError(null);
        setUserModalError(null);
        try {
            const newUsers = [];
            const errors = [];
            for (let i = 0; i < usersData.length; i++) {
                const userData = usersData[i];
                try {
                    console.log(`Processing user ${i + 1}/${usersData.length}:`, userData.email);
                    const result = await processSingleUser(userData, selectedUser, validateUserData, validateUserRoles, updateExistingUser, createNewUser);
                    if (result) {
                        newUsers.push(result);
                        console.log(`Successfully processed user ${i + 1}/${usersData.length}`);
                    }
                }
                catch (userError) {
                    console.error(`Failed to process user ${userData.email}:`, userError);
                    const errorMessage = userError instanceof Error
                        ? userError.message
                        : constants.team_access_error_unexpected;
                    errors.push(`${userData.email}: ${errorMessage}`);
                }
            }
            handleSaveResults(newUsers, errors, setUsers, setUserModalError, setUserModalOpen, selectedUser !== null);
        }
        catch (err) {
            console.error('Unexpected error in handleSaveUser:', err);
            setError(constants.team_access_error_unexpected);
        }
        finally {
            setLoading(false);
        }
    };
    const handleConfirmDelete = async () => {
        if (userToDelete) {
            setLoading(true);
            setError(null);
            try {
                await UserAPI.deleteUser(userToDelete.id);
                setUsers((prevUsers) => prevUsers.filter((u) => u.id !== userToDelete.id));
                setConfirmDialogOpen(false);
                setUserToDelete(null);
            }
            catch (err) {
                console.error('Failed to delete user:', err);
                setError(constants.team_access_error_delete_user);
            }
            finally {
                setLoading(false);
            }
        }
    };
    const handleSelectAll = (checked) => {
        setSelectedUsers(checked ? users.map((u) => u.id) : []);
    };
    const handleSelectUser = (userId, checked) => {
        setSelectedUsers((prev) => checked ? [...prev, userId] : prev.filter((id) => id !== userId));
    };
    const handleRoleChange = async (userId, newRoles) => {
        try {
            // Find the user to get their username
            const user = users.find((u) => u.id === userId);
            if (!user)
                return;
            // Ensure newRoles is always an array, dedupe, and filter empty values
            const rolesArray = Array.from(new Set(Array.isArray(newRoles) ? newRoles : [newRoles])).filter((role) => role && role.trim() !== '');
            // Get current user roles (deduped and filtered)
            const currentRoles = Array.from(new Set(Array.isArray(user.role) ? user.role : [user.role])).filter((role) => role && role.trim() !== '');
            // Find roles to add and remove
            const rolesToAdd = rolesArray.filter((role) => !currentRoles.includes(role));
            const rolesToRemove = currentRoles.filter((role) => !rolesArray.includes(role));
            // Process role additions
            for (const roleName of rolesToAdd) {
                const roleObj = roles.find((r) => r.role_name === roleName);
                if (roleObj) {
                    await UserAPI.assignRole(user.email, {
                        roleId: Number.parseInt(roleObj.role_id)
                    });
                }
            }
            // Process role removals
            for (const roleName of rolesToRemove) {
                const roleObj = roles.find((r) => r.role_name === roleName);
                if (roleObj) {
                    await UserAPI.removeRole(user.id, Number.parseInt(roleObj.role_id));
                    console.log(`Successfully removed role ${roleName} from user ${user.email}`);
                }
            }
            // Update local state
            setUsers((prevUsers) => prevUsers.map((u) => u.id === userId ? { ...u, role: rolesArray } : u));
        }
        catch (err) {
            console.error('Failed to update roles:', err);
            setError(constants.team_access_error_update_user_roles);
        }
    };
    const handleUserDetailsDelete = (user) => {
        setUserToDelete(user);
        setConfirmDialogOpen(true);
        setUserDetailsModalOpen(false);
    };
    const handleWorkspaceRoleChange = async (userId, newWorkspaceRoles) => {
        // Find the user to get their current roles
        const user = users.find((u) => u.id === userId);
        if (!user)
            return;
        // Get current user roles
        const currentRoles = Array.from(new Set(Array.isArray(user.role) ? user.role : [user.role])).filter((role) => role && role.trim() !== '');
        // Helper to check if a role is a workspace role
        const isWorkspaceRole = (roleName) => {
            const roleObj = roles.find((r) => r.role_name === roleName);
            if (!roleObj)
                return false;
            if (!roleObj.application_id)
                return true;
            if (typeof roleObj.application_id === 'string') {
                const trimmed = roleObj.application_id.trim().toLowerCase();
                return (trimmed === '' ||
                    trimmed === '0' ||
                    trimmed === 'null' ||
                    trimmed === 'undefined');
            }
            if (Array.isArray(roleObj.application_id)) {
                if (roleObj.application_id.length === 0)
                    return true;
                return roleObj.application_id.every((id) => {
                    if (id === null || id === undefined)
                        return true;
                    if (typeof id === 'string') {
                        const trimmed = id.trim().toLowerCase();
                        return (trimmed === '' ||
                            trimmed === '0' ||
                            trimmed === 'null' ||
                            trimmed === 'undefined');
                    }
                    return false;
                });
            }
            return false;
        };
        // Keep application roles, replace workspace roles
        const applicationRoles = currentRoles.filter((role) => !isWorkspaceRole(role));
        const combinedRoles = [...newWorkspaceRoles, ...applicationRoles];
        await handleRoleChange(userId, combinedRoles);
    };
    const handleApplicationRoleChange = async (userId, updatedRoles) => {
        // Update the user's roles (this includes both workspace and application roles)
        await handleRoleChange(userId, updatedRoles);
    };
    const handleResendEmail = async (userId) => {
        setLoading(true);
        setError(null);
        try {
            await UserAPI.resendRegistrationEmail(userId);
            // You could add a success notification here
            console.log(`Registration email resent to ${userId}`);
        }
        catch (err) {
            console.error('Failed to resend registration email:', err);
            setError(err instanceof Error
                ? err.message
                : constants.team_access_error_resend_email);
        }
        finally {
            setLoading(false);
        }
    };
    const handleResetPassword = async (userId) => {
        setLoading(true);
        setError(null);
        try {
            await UserAPI.resetPassword(userId);
            // You could add a success notification here
            console.log(`Password reset initiated for ${userId}`);
        }
        catch (err) {
            console.error('Failed to reset password:', err);
            setError(err instanceof Error
                ? err.message
                : constants.team_access_error_reset_password);
        }
        finally {
            setLoading(false);
        }
    };
    // Check view permission
    if (!isAuthorized('RBAC:ViewUser')) {
        return (_jsx(Box, { sx: { p: 3, bgcolor: '#f8f8f8' }, children: _jsxs(Box, { sx: { textAlign: 'center', py: 8 }, children: [_jsx(Typography, { variant: "h6", color: "#666", gutterBottom: true, children: constants.team_access_access_denied_title }), _jsx(Typography, { variant: "body1", color: "#888", children: constants.team_access_access_denied_desc })] }) }));
    }
    return (_jsxs(Box, { sx: { p: 3, bgcolor: '#f8f8f8' }, children: [error && (_jsx(Alert, { severity: "error", sx: { mb: 2 }, onClose: () => setError(null), children: error })), dataLoading ? (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', p: 4 }, children: stateImages?.Loading ? (_jsx(Box, { component: "img", src: stateImages.Loading, alt: "Loading", sx: { width: 128, height: 128 } })) : (_jsx(CircularProgress, {})) })) : (_jsxs(_Fragment, { children: [_jsxs(Paper, { sx: { p: 3, borderRadius: 2, boxShadow: 'none' }, children: [_jsxs(Box, { sx: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    mb: 3
                                }, children: [_jsx(Typography, { variant: "body1", children: constants.team_access_header_description }), _jsxs(Box, { children: [selectedUsers.length > 0 &&
                                                isAuthorized('RBAC:DeleteUser') && (_jsx(Button, { variant: "outlined", sx: {
                                                    color: '#DA291C',
                                                    border: '1px solid #DA291C',
                                                    backgroundColor: 'transparent',
                                                    boxShadow: 'none',
                                                    mr: 1
                                                }, onClick: handleBulkDeleteUsers, children: _jsx(DeleteIcon, {}) })), isAuthorized('RBAC:CreateUser') && (_jsx(Button, { variant: "contained", startIcon: _jsx(AddIcon, {}), onClick: handleAddUser, sx: {
                                                    textTransform: 'none',
                                                    boxShadow: 'none'
                                                }, children: constants.team_access_add_member_button }))] })] }), _jsx(TableContainer, { component: Paper, sx: {
                                    borderRadius: 2,
                                    boxShadow: 'none',
                                    border: 'none'
                                }, children: _jsxs(Table, { sx: { minWidth: 650 }, children: [_jsx(TableHead, { children: _jsxs(TableRow, { children: [_jsx(TableCell, { sx: {
                                                            borderBottom: '1px solid #ebebeb',
                                                            py: 2,
                                                            px: 4,
                                                            minWidth: 200
                                                        }, children: _jsxs(Box, { sx: {
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 2
                                                            }, children: [_jsx(Checkbox, { checked: selectedUsers.length ===
                                                                        users.length, indeterminate: selectedUsers.length >
                                                                        0 &&
                                                                        selectedUsers.length <
                                                                            users.length, onChange: (e) => handleSelectAll(e.target.checked), sx: {
                                                                        color: '#d9d9d9',
                                                                        '&.Mui-checked': {
                                                                            color: '#26890d'
                                                                        }
                                                                    } }), _jsx(Typography, { variant: "body2", sx: {
                                                                        fontWeight: 600,
                                                                        color: '#212121',
                                                                        whiteSpace: 'nowrap'
                                                                    }, children: constants.team_access_table_header_name }), _jsx(FilterIcon, { sx: {
                                                                        fontSize: 18,
                                                                        color: '#26890d'
                                                                    } })] }) }), _jsx(TableCell, { sx: {
                                                            borderBottom: '1px solid #ebebeb',
                                                            py: 2,
                                                            px: 4
                                                        }, children: _jsx(Typography, { variant: "body2", sx: {
                                                                fontWeight: 600,
                                                                color: '#212121'
                                                            }, children: constants.team_access_table_header_email }) }), _jsx(TableCell, { sx: {
                                                            borderBottom: '1px solid #ebebeb',
                                                            py: 2,
                                                            px: 4,
                                                            minWidth: 150
                                                        }, children: _jsx(Typography, { variant: "body2", sx: {
                                                                fontWeight: 600,
                                                                color: '#212121',
                                                                whiteSpace: 'nowrap'
                                                            }, children: constants.team_access_table_header_phone_number }) }), _jsx(TableCell, { sx: {
                                                            borderBottom: '1px solid #ebebeb',
                                                            py: 2,
                                                            px: 4
                                                        }, children: _jsxs(Box, { sx: {
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 1
                                                            }, children: [_jsx(Typography, { variant: "body2", sx: {
                                                                        fontWeight: 600,
                                                                        color: '#212121'
                                                                    }, children: constants.team_access_table_header_role }), _jsx(Tooltip, { title: _jsxs(React.Fragment, { children: [_jsx("strong", { children: constants.team_access_role_tooltip_admin_title }), _jsx("br", {}), constants.team_access_role_tooltip_admin_desc, _jsx("br", {}), _jsx("br", {}), _jsx("strong", { children: constants.team_access_role_tooltip_editor_title }), _jsx("br", {}), constants.team_access_role_tooltip_editor_desc, _jsx("br", {}), _jsx("br", {}), _jsx("strong", { children: constants.team_access_role_tooltip_viewer_title }), _jsx("br", {}), constants.team_access_role_tooltip_viewer_desc, _jsx("br", {}), constants.team_access_role_tooltip_viewer_note] }), placement: "top", slotProps: {
                                                                        popper: {
                                                                            sx: {
                                                                                '& .MuiTooltip-tooltip': {
                                                                                    bgcolor: '#26890D',
                                                                                    color: '#ffffff'
                                                                                }
                                                                            }
                                                                        }
                                                                    }, children: _jsx(InfoIcon, { sx: {
                                                                            fontSize: 20,
                                                                            color: '#26890D',
                                                                            '&:hover': {
                                                                                cursor: 'pointer'
                                                                            }
                                                                        } }) })] }) }), _jsx(TableCell, { sx: {
                                                            borderBottom: '1px solid #ebebeb',
                                                            py: 2,
                                                            px: 4
                                                        }, children: _jsx(Typography, { variant: "body2", sx: {
                                                                fontWeight: 600,
                                                                color: '#212121'
                                                            }, children: constants.team_access_table_header_group }) }), _jsx(TableCell, { sx: {
                                                            borderBottom: '1px solid #ebebeb',
                                                            py: 2,
                                                            px: 4,
                                                            minWidth: 120
                                                        }, children: _jsx(Typography, { variant: "body2", sx: {
                                                                fontWeight: 600,
                                                                color: '#212121',
                                                                whiteSpace: 'nowrap'
                                                            }, children: constants.team_access_table_header_status }) }), _jsx(TableCell, { sx: {
                                                            borderBottom: '1px solid #ebebeb',
                                                            py: 2,
                                                            px: 4,
                                                            minWidth: 180
                                                        }, children: _jsx(Typography, { variant: "body2", sx: {
                                                                fontWeight: 600,
                                                                color: '#212121',
                                                                whiteSpace: 'nowrap'
                                                            }, children: constants.team_access_table_header_registration_email }) }), _jsx(TableCell, { sx: {
                                                            borderBottom: '1px solid #ebebeb',
                                                            py: 2,
                                                            px: 4,
                                                            minWidth: 160
                                                        }, children: _jsx(Typography, { variant: "body2", sx: {
                                                                fontWeight: 600,
                                                                color: '#212121',
                                                                whiteSpace: 'nowrap'
                                                            }, children: constants.team_access_table_header_reset_password }) }), _jsx(TableCell, { sx: {
                                                            borderBottom: '1px solid #ebebeb',
                                                            py: 2,
                                                            px: 4,
                                                            minWidth: 140
                                                        }, children: _jsx(Typography, { variant: "body2", sx: {
                                                                fontWeight: 600,
                                                                color: '#212121',
                                                                whiteSpace: 'nowrap'
                                                            }, children: constants.team_access_table_header_date_added }) }), _jsx(TableCell, { sx: {
                                                            borderBottom: '1px solid #ebebeb',
                                                            py: 2,
                                                            px: 4,
                                                            width: 200
                                                        }, children: _jsx(Typography, { variant: "body2", sx: {
                                                                fontWeight: 600,
                                                                color: '#212121'
                                                            }, children: constants.team_access_table_header_action }) })] }) }), _jsx(TableBody, { children: users.map((user, index) => (_jsxs(TableRow, { sx: {
                                                    borderTop: index > 0
                                                        ? '1px solid #ebebeb'
                                                        : 'none'
                                                }, children: [_jsx(TableCell, { sx: {
                                                            py: 2,
                                                            px: 4,
                                                            height: 55,
                                                            minWidth: 200
                                                        }, children: _jsxs(Box, { sx: {
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                gap: 2
                                                            }, children: [_jsx(Checkbox, { checked: selectedUsers.includes(user.id), onChange: (e) => handleSelectUser(user.id, e.target.checked), sx: {
                                                                        color: '#d9d9d9',
                                                                        '&.Mui-checked': {
                                                                            color: '#26890d'
                                                                        }
                                                                    } }), _jsx(Typography, { variant: "body1", sx: {
                                                                        fontWeight: 600,
                                                                        color: '#26890d',
                                                                        textDecoration: 'underline',
                                                                        cursor: 'pointer',
                                                                        whiteSpace: 'nowrap'
                                                                    }, onClick: () => handleViewUserDetails(user), children: user.name })] }) }), _jsx(TableCell, { sx: {
                                                            py: 2,
                                                            px: 4,
                                                            height: 55
                                                        }, children: _jsx(Typography, { variant: "body1", color: "#212121", children: user.email }) }), _jsx(TableCell, { sx: {
                                                            py: 2,
                                                            px: 4,
                                                            height: 55,
                                                            minWidth: 150
                                                        }, children: _jsx(Typography, { variant: "body1", color: "#212121", children: user.phone || '-' }) }), _jsx(TableCell, { sx: {
                                                            py: 2,
                                                            px: 4,
                                                            height: 55
                                                        }, children: _jsx(Box, { sx: {
                                                                display: 'flex',
                                                                flexWrap: 'wrap',
                                                                gap: 0.5
                                                            }, children: (() => {
                                                                const userRoles = Array.isArray(user.role)
                                                                    ? user.role
                                                                    : [user.role];
                                                                // Dedupe and filter roles
                                                                const validRoles = Array.from(new Set(userRoles.filter((role) => role &&
                                                                    role.trim() !==
                                                                        '')));
                                                                return validRoles.length >
                                                                    0 ? (validRoles.map((roleName) => (_jsx(Chip, { label: roleName, size: "small", sx: {
                                                                        backgroundColor: '#f3f8eb',
                                                                        color: '#1a5d09',
                                                                        fontFamily: 'Open Sans',
                                                                        fontSize: 12,
                                                                        fontWeight: 600,
                                                                        height: 20
                                                                    } }, roleName)))) : (_jsx(Typography, { variant: "body2", sx: {
                                                                        fontSize: '14px',
                                                                        color: '#888'
                                                                    }, children: "-" }));
                                                            })() }) }), _jsx(TableCell, { sx: {
                                                            py: 2,
                                                            px: 4,
                                                            height: 55
                                                        }, children: _jsx(Box, { sx: {
                                                                display: 'flex',
                                                                flexWrap: 'wrap',
                                                                gap: 0.5
                                                            }, children: user.groups &&
                                                                user.groups.length > 0 ? (user.groups.map((groupName) => (_jsx(Chip, { label: groupName, size: "small", sx: {
                                                                    backgroundColor: '#e3f2fd',
                                                                    color: '#1565c0',
                                                                    fontFamily: 'Open Sans',
                                                                    fontSize: 12,
                                                                    fontWeight: 600,
                                                                    height: 20
                                                                } }, groupName)))) : (_jsx(Typography, { variant: "body2", sx: {
                                                                    fontSize: '14px',
                                                                    color: '#888'
                                                                }, children: "-" })) }) }), _jsx(TableCell, { sx: {
                                                            py: 2,
                                                            px: 4,
                                                            height: 55,
                                                            minWidth: 120
                                                        }, children: _jsx(Chip, { label: user.status
                                                                .charAt(0)
                                                                .toUpperCase() +
                                                                user.status.slice(1), size: "small", sx: {
                                                                backgroundColor: getStatusBackgroundColor(user.status),
                                                                color: getStatusTextColor(user.status),
                                                                fontFamily: 'Open Sans',
                                                                fontSize: 12,
                                                                fontWeight: 600,
                                                                height: 20
                                                            } }) }), _jsx(TableCell, { sx: {
                                                            py: 2,
                                                            px: 4,
                                                            height: 55,
                                                            minWidth: 180
                                                        }, children: user.isExternal &&
                                                            user.status ===
                                                                RBAC_CONSTANTS.rbac_user_status_pending ? (_jsx(Button, { variant: "text", startIcon: _jsx(ResendIcon, {}), onClick: () => handleResendEmail(user.id), sx: {
                                                                color: '#26890d',
                                                                textTransform: 'none',
                                                                fontSize: 14,
                                                                fontWeight: 600,
                                                                padding: '8px 12px',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(38, 137, 13, 0.04)'
                                                                }
                                                            }, children: constants.team_access_resend_button })) : (_jsx(Typography, { variant: "body1", color: "#666", children: "-" })) }), _jsx(TableCell, { sx: {
                                                            py: 2,
                                                            px: 4,
                                                            height: 55,
                                                            minWidth: 160
                                                        }, children: user.isExternal &&
                                                            user.status ===
                                                                RBAC_CONSTANTS.rbac_user_status_active ? (_jsx(Button, { variant: "text", startIcon: _jsx(ResetIcon, {}), onClick: () => handleResetPassword(user.id), sx: {
                                                                color: '#26890d',
                                                                textTransform: 'none',
                                                                fontSize: 14,
                                                                fontWeight: 600,
                                                                padding: '8px 12px',
                                                                '&:hover': {
                                                                    backgroundColor: 'rgba(38, 137, 13, 0.04)'
                                                                }
                                                            }, children: constants.team_access_reset_button })) : (_jsx(Typography, { variant: "body1", color: "#666", children: "-" })) }), _jsx(TableCell, { sx: {
                                                            py: 2,
                                                            px: 4,
                                                            height: 55,
                                                            minWidth: 140
                                                        }, children: _jsx(Typography, { variant: "body1", color: "#212121", sx: {
                                                                whiteSpace: 'nowrap'
                                                            }, children: user.dateAdded }) }), _jsx(TableCell, { sx: {
                                                            py: 2,
                                                            px: 4,
                                                            height: 55
                                                        }, children: _jsxs(Box, { sx: {
                                                                display: 'flex',
                                                                gap: 2
                                                            }, children: [isAuthorized('RBAC:UpdateUser') && (_jsx(IconButton, { onClick: () => handleEditUser(user), sx: {
                                                                        color: '#26890d',
                                                                        '&:hover': {
                                                                            bgcolor: 'rgba(38, 137, 13, 0.04)'
                                                                        }
                                                                    }, children: _jsx(EditIcon, { fontSize: "small" }) })), isAuthorized('RBAC:DeleteUser') && (_jsx(IconButton, { onClick: () => handleDeleteUser(user), sx: {
                                                                        color: '#da291c',
                                                                        '&:hover': {
                                                                            bgcolor: 'rgba(218, 41, 28, 0.04)'
                                                                        }
                                                                    }, children: _jsx(DeleteIcon, { fontSize: "small" }) }))] }) })] }, user.id))) })] }) })] }), _jsx(AddUserModal, { open: userModalOpen, onClose: () => {
                            setUserModalOpen(false);
                            setUserModalError(null);
                        }, onSave: handleSaveUser, user: selectedUser, loading: loading, saveError: userModalError }), _jsx(UserDetailsModal, { open: userDetailsModalOpen, onClose: () => setUserDetailsModalOpen(false), user: selectedUserForDetails, onDelete: handleUserDetailsDelete, onWorkspaceRoleChange: handleWorkspaceRoleChange, onApplicationRoleChange: handleApplicationRoleChange }), _jsx(ConfirmationDialog, { isOpen: confirmDialogOpen, setIsOpen: setConfirmDialogOpen, bodyText: constants.team_access_delete_confirm_body(userToDelete?.name ?? ''), submitFunction: handleConfirmDelete, submitButtonText: constants.delete, loading: loading })] }))] }));
};
export default authenticatedRoute(TeamAccess);
